import { useCallback } from 'react';
import type { FormikErrors } from 'formik';
import type { O } from 'ts-toolbelt';

import { checkIfIsArray, checkIfIsInteger } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';

import type { PrintGroup } from '../../../components/DocumentEditor';

import type { PrinterFormValues } from '../types/printer-form-values.type';

interface Params {
  readonly contentToPrint: readonly PrintGroup[];
}

export const useValidate = ({ contentToPrint }: Readonly<Params>) => {
  const printGroupsCount = contentToPrint.length;

  const validate = useCallback(
    (values: Readonly<PrinterFormValues>) => {
      const errors: FormikErrors<O.Writable<PrinterFormValues>> = {};

      // step
      if (!values.step || values.step === EMPTY_STRING) {
        errors.step = "Étape d'impression requise.";
      } else if (!['0', '1'].includes(values.step)) {
        errors.step = "Étape d'impression invalide.";
      }

      // printGroupsCount
      if (!values.printGroupsCount) {
        errors.printGroupsCount = "Nombre de groupes d'impression requis.";
      } else if (values.printGroupsCount < 0) {
        errors.printGroupsCount = "Nombre de groupes d'impression invalide.";
      }

      // currentGroupIndex
      if (
        !values.currentGroupIndex ||
        values.currentGroupIndex === EMPTY_STRING
      ) {
        errors.currentGroupIndex = 'Index du groupe à imprimer requis.';
      } else if (
        values.currentGroupIndex &&
        (Number.isNaN(Number.parseInt(values.currentGroupIndex, 10)) ||
          Number.parseInt(values.currentGroupIndex, 10) < 0 ||
          Number.parseInt(values.currentGroupIndex, 10) > printGroupsCount - 1)
      ) {
        errors.currentGroupIndex = 'Index du groupe à imprimer invalide.';
      }

      // sectionTitlesList
      const parsedSectionTitlesList: string[] = [];
      if (
        !values.sectionTitlesList ||
        values.sectionTitlesList === EMPTY_STRING
      ) {
        errors.sectionTitlesList = 'Liste des titres de section requise.';
      } else {
        const parsed = JSON.parse(values.sectionTitlesList) as unknown;

        if (!checkIfIsArray(parsed)) {
          errors.sectionTitlesList = 'Liste des titres de section invalide.';
        } else {
          // eslint-disable-next-line functional/no-loop-statements
          for (const sectionTitle of parsed) {
            if (typeof sectionTitle !== 'string') {
              errors.sectionTitlesList =
                'Liste des titres de section invalide.';
            } else {
              parsedSectionTitlesList.push(sectionTitle);
            }
          }
        }
      }

      // sectionDisplayFirstPageNumbers
      if (
        values.step === '1' &&
        (!values.sectionDisplayFirstPageNumbers ||
          values.sectionDisplayFirstPageNumbers === EMPTY_STRING)
      ) {
        errors.sectionDisplayFirstPageNumbers =
          'La table des numéros de page (affiché) est obligatoire lors de la seconde étape.';
      }
      if (values.sectionDisplayFirstPageNumbers) {
        const parsedSectionDisplayFirstPageNumbers = JSON.parse(
          values.sectionDisplayFirstPageNumbers,
        ) as unknown;

        if (
          !checkIfIsArray(parsedSectionDisplayFirstPageNumbers) ||
          parsedSectionDisplayFirstPageNumbers.length < 1
        ) {
          errors.sectionDisplayFirstPageNumbers =
            "La table des numéros de page (affiché) n'a pas un format valide.";
        } else if (
          parsedSectionDisplayFirstPageNumbers.length !==
          parsedSectionTitlesList.length
        ) {
          errors.sectionDisplayFirstPageNumbers =
            "La taille de la table des numéros de première page (affiché) n'est pas égale au nombre de titres de section.";
        } else {
          // eslint-disable-next-line functional/no-let
          let maxPageNumber = 0;

          parsedSectionDisplayFirstPageNumbers.forEach(
            (pageNumber: unknown) => {
              if (!checkIfIsInteger(pageNumber)) {
                errors.sectionDisplayFirstPageNumbers =
                  "La table des numéros de page (affiché) n'a pas un format valide.";
              } else {
                if (Number.isNaN(pageNumber) || pageNumber < 0) {
                  errors.sectionDisplayFirstPageNumbers =
                    'Numéro de page de début de section (affiché) invalide.';
                } else if (pageNumber < maxPageNumber) {
                  errors.sectionDisplayFirstPageNumbers =
                    'Les numéros de page de début de section (affiché) doivent être croissants.';
                }
                maxPageNumber = pageNumber;
              }
            },
          );
        }
      }

      // totalDisplayPageCount
      if (
        values.step === '1' &&
        (!values.totalDisplayPageCount ||
          values.totalDisplayPageCount === EMPTY_STRING)
      ) {
        errors.totalDisplayPageCount =
          'Nombre total (affiché) de pages requis lors de la seconde étape.';
      } else if (
        values.totalDisplayPageCount &&
        Number.isNaN(Number.parseInt(values.totalDisplayPageCount, 10))
      ) {
        errors.totalDisplayPageCount =
          'Nombre total (affiché) de pages invalide.';
      }

      // fileCategory
      if (!values.fileCategoryId || values.fileCategoryId === EMPTY_STRING) {
        errors.fileCategoryId = 'Identifiant de catégorie de fichier requis.';
      }

      // style
      if (!values.styleId || values.styleId === EMPTY_STRING) {
        errors.styleId = "Identifiant de style d'impression requis.";
      }

      if (!values.styleCode || values.styleCode === EMPTY_STRING) {
        errors.styleCode = "Code de style d'impression requis.";
      }

      return errors;
    },
    [printGroupsCount],
  );

  return { validate };
};
